import { useEffect, useRef } from 'react'
import wsService from '@services/websocket-service'

type EventCallback = (...args: any[]) => void

interface UseWebSocketOptions {
  events?: { [event: string]: EventCallback }
  storeId?: string
  autoConnect?: boolean
}

export const useWebSocket = (options: UseWebSocketOptions = {}) => {
  const { events = {}, storeId, autoConnect = true } = options
  const unsubscribesRef = useRef<Array<() => void>>([])

  useEffect(() => {
    // Connect if needed
    if (autoConnect && !wsService.isConnected()) {
      wsService.connect()
    }

    // Subscribe to events
    Object.entries(events).forEach(([event, callback]) => {
      const unsubscribe = wsService.on(event as any, callback)
      unsubscribesRef.current.push(unsubscribe)
    })

    // Subscribe to store if provided
    if (storeId) {
      wsService.subscribeToStore(storeId)
    }

    // Cleanup
    return () => {
      // Unsubscribe from events
      unsubscribesRef.current.forEach(unsubscribe => unsubscribe())
      unsubscribesRef.current = []

      // Unsubscribe from store
      if (storeId) {
        wsService.unsubscribeFromStore(storeId)
      }
    }
  }, [])

  return {
    connected: wsService.isConnected(),
    send: wsService.send.bind(wsService),
    requestRefresh: wsService.requestRefresh.bind(wsService)
  }
}

// Hook for revenue updates
export const useRevenueUpdates = (onUpdate: (data: any) => void) => {
  return useWebSocket({
    events: {
      'revenue:update': onUpdate
    }
  })
}

// Hook for agent status updates
export const useAgentStatusUpdates = (onUpdate: (data: any) => void) => {
  return useWebSocket({
    events: {
      'agent:status': onUpdate
    }
  })
}

// Hook for system health updates
export const useSystemHealthUpdates = (onUpdate: (data: any) => void) => {
  return useWebSocket({
    events: {
      'system:health': onUpdate
    }
  })
}